import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './VolunteerForm.css';

const VolunteerForm = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    expertise: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[\d\s\-+()]+$/;
    return re.test(phone) && phone.replace(/\D/g, '').length >= 9;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = language === 'ar' ? 'الاسم الكامل مطلوب' : 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = language === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = language === 'ar' ? 'البريد الإلكتروني غير صالح' : 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = language === 'ar' ? 'رقم الهاتف غير صالح' : 'Invalid phone number';
    }

    if (!formData.expertise.trim()) {
      newErrors.expertise = language === 'ar' ? 'مجال الخبرة مطلوب' : 'Area of expertise is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = language === 'ar' ? 'الرسالة مطلوبة' : 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const form = e.target;
    const formSubmitData = new FormData(form);

    try {
      await fetch('https://formsubmit.co/info@mandula.org', {
        method: 'POST',
        body: formSubmitData
      });

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        expertise: '',
        message: ''
      });

      alert(language === 'ar' 
        ? 'تم إرسال طلب التطوع بنجاح! سنتواصل معك قريباً.' 
        : 'Your volunteer application has been submitted successfully! We will contact you soon.');
    } catch (error) {
      alert(language === 'ar' 
        ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' 
        : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-card">
      <div className="form-header">
        <h2>{language === 'ar' ? 'انضم كمتطوع' : 'Volunteer With Us'}</h2>
        <p>{language === 'ar' ? 'انضم إلى فريقنا وساعدنا في إحداث فرق' : 'Join our team and help us make a difference'}</p>
      </div>

      <form onSubmit={handleSubmit} className="modern-form">
        <input type="hidden" name="_captcha" value="false" />
        <input type="hidden" name="_template" value="table" />
        <input type="hidden" name="_subject" value="New Volunteer Application - MRRO" />

        <div className="form-field">
          <label htmlFor="fullName">
            {language === 'ar' ? 'الاسم الكامل' : 'Full Name'} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={errors.fullName ? 'error' : ''}
            placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
          />
          {errors.fullName && <span className="error-text">{errors.fullName}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="email">
            {language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'} <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
            placeholder={language === 'ar' ? 'example@email.com' : 'example@email.com'}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="phone">
            {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'} <span className="required">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? 'error' : ''}
            placeholder={language === 'ar' ? '+249 123 456 789' : '+249 123 456 789'}
          />
          {errors.phone && <span className="error-text">{errors.phone}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="expertise">
            {language === 'ar' ? 'مجال الخبرة أو المهارات' : 'Area of Expertise or Skills'} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="expertise"
            name="expertise"
            value={formData.expertise}
            onChange={handleChange}
            className={errors.expertise ? 'error' : ''}
            placeholder={language === 'ar' ? 'مثال: الصحة، التعليم، إدارة المشاريع' : 'e.g., Healthcare, Education, Project Management'}
          />
          {errors.expertise && <span className="error-text">{errors.expertise}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="message">
            {language === 'ar' ? 'لماذا تريد التطوع معنا؟' : 'Why do you want to volunteer with us?'} <span className="required">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className={errors.message ? 'error' : ''}
            rows="5"
            placeholder={language === 'ar' ? 'أخبرنا لماذا تريد التطوع مع منظمة المندولة...' : 'Tell us why you want to volunteer with MRRO...'}
          ></textarea>
          {errors.message && <span className="error-text">{errors.message}</span>}
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting 
            ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
            : (language === 'ar' ? 'إرسال الطلب' : 'Submit Application')}
        </button>
      </form>
    </div>
  );
};

export default VolunteerForm;
